import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const studioId = params.get("studio") || "";
  const [studio, setStudio] = useState(null);

  useEffect(() => {
    if (!studioId) return;
    api.get(`/public/studio-privacy/${encodeURIComponent(studioId)}`)
      .then((r) => setStudio(r.data))
      .catch(() => {});
  }, [studioId]);

  const hasStudio = studio && (studio.ragione_sociale || studio.email);
  const indirizzoParts = studio
    ? [studio.indirizzo, [studio.cap, studio.citta].filter(Boolean).join(" "), studio.provincia && `(${studio.provincia})`].filter(Boolean).join(", ")
    : "";

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        <button onClick={() => navigate(-1)} data-testid="privacy-back"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4" />Indietro
        </button>

        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="h-6 w-6 text-[#007BFF]" />
          <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight">Informativa sul trattamento dei dati personali</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-8">
          Ai sensi degli artt. 13 e 14 del Regolamento (UE) 2016/679 (GDPR). Ultimo aggiornamento: giugno 2026.
        </p>

        <div className="space-y-6 text-[15px] leading-relaxed [&_h2]:font-head [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-6 [&_h2]:mb-1.5 [&_p]:text-muted-foreground [&_li]:text-muted-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1">
          <section>
            <h2>1. Titolare del trattamento</h2>
            {hasStudio ? (
              <div className="rounded-lg border border-border bg-card p-4 text-sm not-italic" data-testid="privacy-studio-box">
                {studio.ragione_sociale && <p className="text-foreground font-semibold">{studio.ragione_sociale}</p>}
                {indirizzoParts && <p>{indirizzoParts}</p>}
                {studio.partita_iva && <p>P.IVA {studio.partita_iva}</p>}
                {studio.email && <p>Email: <a href={`mailto:${studio.email}`} className="text-[#007BFF] hover:underline">{studio.email}</a></p>}
                {studio.pec && <p>PEC: {studio.pec}</p>}
                {studio.telefono && <p>Tel: {studio.telefono}</p>}
              </div>
            ) : (
              <p>Il titolare del trattamento è lo studio di amministrazione condominiale che gestisce il servizio tramite la piattaforma Sedio. I riferimenti completi del titolare sono disponibili su richiesta e nelle comunicazioni ricevute dallo studio.</p>
            )}
          </section>
          <section>
            <h2>2. Tipologie di dati trattati</h2>
            <ul>
              <li>Dati anagrafici e di contatto (nome, cognome, codice fiscale, email, numero di telefono).</li>
              <li>Dati relativi all'unità immobiliare e al condominio (scala, interno, millesimi, ruolo proprietario/inquilino).</li>
              <li>Dati catastali dell'immobile di cui risulti intestatario e dei relativi comproprietari (es. foglio, particella, subalterno, categoria, rendita), trattati per le finalità di amministrazione.</li>
              <li>Contenuto delle segnalazioni e delle comunicazioni scambiate con l'amministrazione (email, messaggi SMS, messaggi del portale) ed eventuali allegati.</li>
              <li>Credenziali e dati tecnici di accesso al portale (log di autenticazione) e cronologia delle conversazioni con l'assistente virtuale.</li>
              <li>Dati relativi all'eventuale acquisto di visure tramite il portale (il pagamento è gestito da un fornitore esterno; non vengono conservati i dati completi della carta).</li>
            </ul>
          </section>
          <section>
            <h2>3. Finalità del trattamento</h2>
            <p>I dati sono trattati per: gestione delle pratiche e segnalazioni condominiali; comunicazioni verso i condòmini via email/SMS/portale; gestione dell'anagrafica e dei dati catastali; erogazione del portale riservato e dell'assistente virtuale; acquisto di visure richieste dall'interessato; adempimenti amministrativi e contabili; assistenza e supporto; ed eventuali obblighi di legge.</p>
          </section>
          <section>
            <h2>4. Base giuridica</h2>
            <p>Il trattamento si fonda sull'esecuzione del contratto/mandato di amministrazione, sull'adempimento di obblighi legali e, ove previsto, sul consenso dell'interessato ai sensi dell'art. 6 del GDPR.</p>
          </section>
          <section>
            <h2>5. Modalità e conservazione</h2>
            <p>I dati sono trattati con strumenti informatici e misure di sicurezza adeguate. Sono conservati per il tempo necessario alle finalità indicate e nel rispetto dei termini di legge, dopodiché vengono cancellati o anonimizzati.</p>
          </section>
          <section>
            <h2>6. Comunicazione dei dati e fornitori tecnologici</h2>
            <p>I dati possono essere comunicati a fornitori incaricati degli interventi, professionisti e consulenti dello studio, e a fornitori tecnologici che agiscono in qualità di responsabili del trattamento per conto dello studio, esclusivamente per le finalità sopra indicate. Tra questi: la piattaforma <strong>Sedio</strong> (infrastruttura e servizi software), fornitori di posta e messaggistica (email, SMS), fornitori di modelli di intelligenza artificiale (per triage, assistente e importazione anagrafiche), il provider della banca dati catastale (per visure e dati catastali) e il fornitore di pagamento (per l'acquisto di visure). Alcuni trattamenti possono comportare trasferimenti extra-UE assistiti da adeguate garanzie. I dati non sono diffusi né ceduti a terzi per finalità di marketing.</p>
          </section>
          <section>
            <h2>7. Intelligenza artificiale</h2>
            <p>Alcune funzioni (assistente del portale, triage e importazione dati) utilizzano sistemi di intelligenza artificiale con finalità di ausilio. Le risposte dell'assistente sono generate automaticamente, possono contenere imprecisioni e non sostituiscono le comunicazioni ufficiali dell'amministrazione. È sempre garantito il diritto di rivolgersi a una persona fisica dello studio.</p>
          </section>
          <section>
            <h2>8. Diritti dell'interessato</h2>
            <p>In qualsiasi momento puoi esercitare i diritti previsti dagli artt. 15-22 del GDPR: accesso, rettifica, cancellazione, limitazione, portabilità, opposizione e revoca del consenso. Puoi inoltre proporre reclamo all'Autorità Garante per la protezione dei dati personali.</p>
          </section>
          <section>
            <h2>9. Contatti</h2>
            {hasStudio && (studio.email || studio.pec) ? (
              <p>Per esercitare i tuoi diritti o per qualsiasi informazione sul trattamento dei tuoi dati, contatta
                {studio.ragione_sociale ? ` ${studio.ragione_sociale}` : " lo studio di amministrazione"}
                {studio.email && <> all'indirizzo <a href={`mailto:${studio.email}`} className="text-[#007BFF] hover:underline">{studio.email}</a></>}
                {studio.pec && <> (PEC: {studio.pec})</>}.
              </p>
            ) : (
              <p>Per esercitare i tuoi diritti o per qualsiasi informazione sul trattamento dei tuoi dati, contatta lo studio di amministrazione che gestisce il tuo condominio tramite i recapiti indicati nelle sue comunicazioni.</p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
